// ============================================================
// TanStack / React Query Hooks for Incomes & Categories
// ============================================================

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { incomeService } from '@/services/incomeService';
import { useSchool } from './useSchool';

/**
 * Fetch all income categories
 */
export function useIncomeCategories(includeInactive = true) {
  const { schoolId } = useSchool();

  return useQuery({
    queryKey: ['incomeCategories', schoolId, includeInactive],
    queryFn: () => incomeService.getCategories(schoolId, includeInactive),
    enabled: !!schoolId,
  });
}

/**
 * Create income category mutation
 */
export function useCreateIncomeCategory() {
  const { schoolId } = useSchool();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload) => incomeService.createCategory(schoolId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['incomeCategories', schoolId] });
    },
  });
}

/**
 * Update income category mutation
 */
export function useUpdateIncomeCategory() {
  const { schoolId } = useSchool();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, payload }) => incomeService.updateCategory(schoolId, id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['incomeCategories', schoolId] });
      queryClient.invalidateQueries({ queryKey: ['incomes', schoolId] });
    },
  });
}

/**
 * Delete income category mutation
 */
export function useDeleteIncomeCategory() {
  const { schoolId } = useSchool();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id) => incomeService.deleteCategory(schoolId, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['incomeCategories', schoolId] });
    },
  });
}

/**
 * Fetch incomes list with optional filters
 */
export function useIncomes(filters = {}) {
  const { schoolId } = useSchool();

  return useQuery({
    queryKey: ['incomes', schoolId, filters],
    queryFn: () => incomeService.getIncomes(schoolId, filters),
    enabled: !!schoolId,
  });
}

/**
 * Fetch single income by ID
 */
export function useIncome(id) {
  const { schoolId } = useSchool();

  return useQuery({
    queryKey: ['income', schoolId, id],
    queryFn: () => incomeService.getIncomeById(schoolId, id),
    enabled: !!schoolId && !!id,
  });
}

/**
 * Create income mutation
 */
export function useCreateIncome() {
  const { schoolId } = useSchool();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload) => incomeService.createIncome(schoolId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['incomes', schoolId] });
      queryClient.invalidateQueries({ queryKey: ['incomeSummaryTotal', schoolId] });
    },
  });
}

/**
 * Update income mutation
 */
export function useUpdateIncome() {
  const { schoolId } = useSchool();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, payload }) => incomeService.updateIncome(schoolId, id, payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['incomes', schoolId] });
      queryClient.invalidateQueries({ queryKey: ['income', schoolId, variables.id] });
      queryClient.invalidateQueries({ queryKey: ['incomeSummaryTotal', schoolId] });
    },
  });
}

/**
 * Delete income mutation
 */
export function useDeleteIncome() {
  const { schoolId } = useSchool();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id) => incomeService.deleteIncome(schoolId, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['incomes', schoolId] });
      queryClient.invalidateQueries({ queryKey: ['incomeSummaryTotal', schoolId] });
    },
  });
}

/**
 * Fetch total income summary
 */
export function useIncomeSummaryTotal(filters = {}) {
  const { schoolId } = useSchool();

  return useQuery({
    queryKey: ['incomeSummaryTotal', schoolId, filters],
    queryFn: () => incomeService.getSummaryTotal(schoolId, filters),
    enabled: !!schoolId,
  });
}
